import { Component, OnInit } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { Observable } from "rxjs";
import { filter, map, startWith } from "rxjs/operators";

import {
    GetCollectionsQuery,
    GetCollectionsQueryVariables,
    SignOutMutation,
} from "./common/generated-types";
import { GET_COLLECTIONS } from "./common/graphql/documents.graphql";
import { DataService } from "./core/providers/data/data.service";
import { StateService } from "./core/providers/state/state.service";
import { GetAccountOverviewQuery } from "./common/generated-types";
import { GET_ACCOUNT_OVERVIEW } from "./app.graphql";
import { notNullOrUndefined } from "./common/utils/not-null-or-undefined";
import { SIGN_OUT } from "./account/components/account/account.graphql";

@Component({
    selector: "vsf-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit {
    cartDrawerVisible$: Observable<boolean>;
    mobileNavVisible$: Observable<boolean>;
    isHomePage$: Observable<boolean>;
    isSellerPortal$: Observable<boolean>;
    topCollections$: Observable<GetCollectionsQuery["collections"]["items"]>;
    currentYear: number;
    mobileMenuVisible = false;
    categoriesVisible = false;
    user: GetAccountOverviewQuery["activeCustomer"];

    get isProductPage(): boolean { return this.router.url.startsWith("/product/"); }

    navigation = {
        support: [
            { name: "Help", href: "#" },
            { name: "Track order", href: "#" },
            { name: "Shipping", href: "#" },
            { name: "Returns", href: "#" },
        ],
        company: [
            { name: "About", href: "#" },
            { name: "Blog", href: "#" },
            { name: "Corporate responsibility", href: "#" },
            { name: "Press", href: "#" },
        ],
    };

    constructor(
        private router: Router,
        private stateService: StateService,
        private dataService: DataService
    ) {}

    private toggleBodyScroll(disable: boolean) {
        if (disable) {
            document.body.classList.add("menu-open");
        } else {
            document.body.classList.remove("menu-open");
        }
    }

    closeAllMenus() {
        this.categoriesVisible = false;
        this.mobileMenuVisible = false;
        this.toggleBodyScroll(false);
    }

    closeCategories() {
        this.categoriesVisible = false;
        this.mobileMenuVisible = true;
        this.toggleBodyScroll(true);
    }

    openCategories() {
        this.categoriesVisible = true;
        this.mobileMenuVisible = false;
        this.toggleBodyScroll(true);
    }

    toggleMobileMenu() {
        this.mobileMenuVisible = !this.mobileMenuVisible;
        this.toggleBodyScroll(this.mobileMenuVisible);
    }

    ngOnInit(): void {
        this.currentYear = new Date().getFullYear();

        this.cartDrawerVisible$ = this.stateService.select(
            (state) => state.cartDrawerOpen
        );
        this.mobileNavVisible$ = this.stateService.select(
            (state) => state.mobileNavMenuIsOpen
        );
        this.isHomePage$ = this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map((event) => this.isHomeUrl(event.urlAfterRedirects)),
            startWith(this.isHomeUrl(this.router.url))
        );
        this.isSellerPortal$ = this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map((event) => this.isSellerPortalUrl(event.urlAfterRedirects)),
            startWith(this.isSellerPortalUrl(this.router.url))
        );

        this.topCollections$ = this.dataService
            .query<GetCollectionsQuery, GetCollectionsQueryVariables>(
                GET_COLLECTIONS,
                {
                    options: { take: 25, topLevelOnly: true },
                }
            )
            .pipe(map(({ collections }) => collections.items));

        this.dataService
            .query<GetAccountOverviewQuery>(GET_ACCOUNT_OVERVIEW)
            .pipe(
                map((data) => data.activeCustomer),
                filter(notNullOrUndefined)
            )
            .subscribe((customer) => {
                this.user = customer;

                const customerWithUser = customer as any;
                if (customerWithUser.user?.customFields?.isApproved === false) {
                    this.dataService
                        .mutate<SignOutMutation>(SIGN_OUT)
                        .subscribe({
                            next: () => {
                                this.stateService.setState("signedIn", false);
                                this.router.navigate(["/"]).then(() => {
                                    window.location.reload();
                                });
                            },
                        });
                }
            });
    }

    private isHomeUrl(url: string): boolean {
        const path = url.split("?")[0].split("#")[0];
        return path === "/" || path === "";
    }

    private isSellerPortalUrl(url: string): boolean {
        const path = url.split("?")[0].split("#")[0];
        return path === "/seller" || path.startsWith("/seller/");
    }

    navigateToCart() {
        this.router.navigate(["/cart"]);
    }

    signOut() {
        this.dataService.mutate<SignOutMutation>(SIGN_OUT).subscribe({
            next: () => {
                this.stateService.setState("signedIn", false);
                this.closeAllMenus();
                this.router.navigate(["/"]).then(() => {
                    window.location.reload();
                });
            },
        });
    }
}
